import React, { useEffect, useState } from 'react'
import moment from 'moment/moment'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { DateTimePicker } from '@mui/x-date-pickers'
import { apiGet, apiPost } from '../../../generic/Api_Functions'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../generic/hooks/useDateFormatter'
import PriorityConditionChecker from '../../../generic/utilities/PriorityConditionChecker'
import { fabricationOrderSaveErrorMessage } from '../fabricationOrderSaveError'

// "Copy as" from the detail, with the SAME logic as the legacy row action
// (`FabricationOrder.jsx` handleCopyButtonClick / handleAcceptButtonClick): the whole row is
// copied under `<code>-<n>`, the user sets quantity, target date and priority, and the copy is
// CREATED. The detail used to stop at the copy number and announce a creation that never happened.
// `sheetStatus` is 'ready' unless the row is read when the dialog opens: while it is 'loading' the
// dialog waits for it, and 'error' closes it with the same kind of notice as a failed copy number.
const CopyFabricationOrderDialog = ({ open, sheet, sheetStatus = 'ready', onClose, showAlert }) => {
  const { t_ } = useSmartTranslation()
  const { format } = useDateFormatter()
  const [copyCode, setCopyCode] = useState('')
  const [form, setForm] = useState({ quantity: '', targetDate: null, priority: '' })
  const [error, setError] = useState('')

  const sheetReady = sheetStatus === 'ready'

  useEffect(() => {
    if (open && sheetStatus === 'error') {
      showAlert({ severity: 'error', title: t_('Error loading data'), message: '' })
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sheetStatus])

  useEffect(() => {
    if (!open || !sheetReady) return
    setError('')
    setCopyCode('')
    setForm({ quantity: sheet.quantity, targetDate: null, priority: sheet.priority })
    apiGet('getFabricationOrderNumberCopy/' + encodeURIComponent(sheet.fabricationOrder))
      .then((copyNumber) => setCopyCode(`${sheet.fabricationOrder}-${copyNumber}`))
      .catch((failure) => {
        showAlert({ severity: 'error', title: t_('Error generating copy number'), message: t_(failure?.message || '') })
        onClose()
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sheetReady])

  const handleAccept = async () => {
    // The code arrives asynchronously; the legacy only opened once it had it.
    if (!copyCode) return
    if (!form.targetDate) {
      setError(t_('Delivery date cannot be null'))
      return
    }
    const orderDate = moment(sheet.date)
    const targetDate = moment(form.targetDate)
    if (!targetDate.isValid() || targetDate.isBefore(orderDate)) {
      setError(t_('Delivery date must be later than the order date'))
      return
    }
    const priorityError = PriorityConditionChecker(form.priority, t_)
    if (priorityError !== '') {
      setError(priorityError)
      return
    }
    const copy = { ...sheet, fabricationOrder: copyCode, targetDate: form.targetDate, quantity: form.quantity, priority: form.priority, id: null }
    onClose()
    try {
      await apiPost('fabricationOrder', copy)
      showAlert({ severity: 'success', title: '', message: t_('Copied correctly') })
    } catch (failure) {
      showAlert({ severity: 'error', title: t_('Error creating fabrication order'), message: fabricationOrderSaveErrorMessage(failure, t_) })
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>{t_('Copy as')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={3}><TextField fullWidth margin='normal' label={t_('Fabrication order')} value={copyCode} disabled /></Grid>
          <Grid item xs={3}>
            <TextField fullWidth margin='normal' label={t_('Quantity')} type='number' value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
          </Grid>
          <Grid item xs={3} sx={{ mt: 2 }}>
            <DateTimePicker label={t_('Target date')} value={form.targetDate} format={format.dateTime} onChange={(value) => setForm({ ...form, targetDate: value })} />
          </Grid>
          <Grid item xs={3}>
            <TextField fullWidth margin='normal' label={t_('Priority')} type='number' inputProps={{ min: 1, max: 10 }} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
          </Grid>
        </Grid>
        {!sheetReady && <Typography data-testid='copy-loading' variant='caption' color='text.secondary'>{t_('Loading...')}</Typography>}
        {!!error && <Typography data-testid='copy-error' variant='caption' color='error'>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t_('Cancel')}</Button>
        <Button data-testid='copy-accept' disabled={!sheetReady || !copyCode} variant='contained' onClick={handleAccept}>{t_('Accept')}</Button>
      </DialogActions>
    </Dialog>
  )
}

export default CopyFabricationOrderDialog
